import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Row, Col, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createMilestone,
  updateMilestone,
  getMilestoneTypes,
  getMilestoneStatuses,
  getMilestoneTypesByOrg,
  getMilestoneStatusesByOrg,
} from "../Api/MilestoneMaster";
import { getTasksByProject } from "../Api/TaskMaster";
import {
  FaFlag,
  FaTasks,
  FaCalendarAlt,
  FaPercentage,
  FaInfoCircle,
  FaProjectDiagram,
  FaBuilding,
  FaUser,
  FaTimes,
  FaCheck,
  FaArrowRight,
  FaClock, // Add this import
} from "react-icons/fa";
import "./MilestoneModal.css";

const MilestoneModal = ({ show, onHide, milestone, projectId, onMilestoneSaved }) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;
  const userName = userDetails?.username || "System";

  const [formData, setFormData] = useState({
    name: "",
    Type: "",
    percentage: "",
    plannedStartDate: "",
    plannedEndDate: "",
    actualStartDate: "",
    actualEndDate: "",
    prozmaPrId: projectId || "",
    Status: "",
    description: "",
    orgId: String(orgId) || "",
    taskAssigned: [],
    hour: "", // Add hour field
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [searchTaskTerm, setSearchTaskTerm] = useState("");

  const [milestoneTypes, setMilestoneTypes] = useState([]);
  const [milestoneStatuses, setMilestoneStatuses] = useState([]);
  const [loadingMetaData, setLoadingMetaData] = useState(false);

  const [typeNameMap, setTypeNameMap] = useState({});
  const [statusNameMap, setStatusNameMap] = useState({});

  useEffect(() => {
    if (show && orgId) {
      fetchMilestoneTypes();
      fetchMilestoneStatuses();
    }
  }, [show, orgId]);

  const fetchMilestoneTypes = async () => {
    if (!orgId) {
      console.warn("No orgId available to fetch milestone types");
      return;
    }

    try {
      setLoadingMetaData(true);
      const types = await getMilestoneTypesByOrg(orgId);
      const typesArray = Array.isArray(types) ? types : types?.data || [];
      setMilestoneTypes(typesArray);

      const typeMap = {};
      typesArray.forEach((type) => {
        const guid = type.milestoneType_GUID || type.guid;
        const name = type.milestoneType_Name || type.name;
        if (guid && name) {
          typeMap[guid.toLowerCase()] = name;
        }
      });
      setTypeNameMap(typeMap);
    } catch (error) {
      console.error("Error fetching milestone types:", error);
      toast.error("Failed to load milestone types");
    } finally {
      setLoadingMetaData(false);
    }
  };

  const fetchMilestoneStatuses = async () => {
    if (!orgId) {
      console.warn("No orgId available to fetch milestone statuses");
      return;
    }

    try {
      setLoadingMetaData(true);
      const statuses = await getMilestoneStatusesByOrg(orgId);
      const statusesArray = Array.isArray(statuses)
        ? statuses
        : statuses?.data || [];
      setMilestoneStatuses(statusesArray);

      const statusMap = {};
      statusesArray.forEach((status) => {
        const guid = status.statusGuid || status.guid;
        const name = status.statusName || status.name;
        if (guid && name) {
          statusMap[guid.toLowerCase()] = name;
        }
      });
      setStatusNameMap(statusMap);
    } catch (error) {
      console.error("Error fetching milestone statuses:", error);
      toast.error("Failed to load milestone statuses");
    } finally {
      setLoadingMetaData(false);
    }
  };

  useEffect(() => {
    if (show && projectId) {
      fetchTasks();
    }
  }, [show, projectId]);

  useEffect(() => {
    if (milestone) {
      let taskAssignedArray = [];

      if (
        milestone.taskAssignedList &&
        Array.isArray(milestone.taskAssignedList)
      ) {
        taskAssignedArray = milestone.taskAssignedList;
      } else if (
        milestone.taskAssigned &&
        Array.isArray(milestone.taskAssigned)
      ) {
        taskAssignedArray = milestone.taskAssigned;
      } else if (
        milestone.taskAssigned &&
        typeof milestone.taskAssigned === "string"
      ) {
        taskAssignedArray = milestone.taskAssigned
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t);
      }

      let typeGuid = "";
      if (milestone.Type) {
        typeGuid = milestone.Type;
      } else if (milestone.type) {
        typeGuid = milestone.type;
      } else if (milestone.milestoneType_GUID) {
        typeGuid = milestone.milestoneType_GUID;
      }

      let statusGuid = "";
      if (milestone.Status) {
        statusGuid = milestone.Status;
      } else if (milestone.status) {
        statusGuid = milestone.status;
      } else if (milestone.statusGuid) {
        statusGuid = milestone.statusGuid;
      }

      const formatDateFromApi = (dateValue) => {
        if (!dateValue) return "";
        if (
          typeof dateValue === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
        ) {
          return dateValue;
        }
        if (typeof dateValue === "string" && dateValue.includes("T")) {
          return dateValue.split("T")[0];
        }
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return "";
          return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
        } catch {
          return "";
        }
      };

      setFormData({
        name: milestone.name || "",
        Type: typeGuid,
        percentage: milestone.percentage || "",
        plannedStartDate: formatDateFromApi(milestone.plannedStartDate),
        plannedEndDate: formatDateFromApi(milestone.plannedEndDate),
        actualStartDate: formatDateFromApi(milestone.actualStartDate),
        actualEndDate: formatDateFromApi(milestone.actualEndDate),
        prozmaPrId: milestone.prozmaPrId || projectId || "",
        Status: statusGuid,
        description: milestone.description || "",
        orgId: milestone.orgId || String(orgId) || "",
        taskAssigned: taskAssignedArray,
        hour: milestone.hour !== undefined && milestone.hour !== null ? milestone.hour : "",
      });

      setSelectedTaskIds(new Set(taskAssignedArray));
    } else {
      setFormData({
        name: "",
        Type: "",
        percentage: "",
        plannedStartDate: "",
        plannedEndDate: "",
        actualStartDate: "",
        actualEndDate: "",
        prozmaPrId: projectId || "",
        Status: "",
        description: "",
        orgId: String(orgId) || "",
        taskAssigned: [],
        hour: "",
      });
      setSelectedTaskIds(new Set());
    }
    setErrors({});
    setSearchTaskTerm("");
  }, [milestone, show, projectId, orgId]);

  const fetchTasks = async () => {
    if (!projectId) return;

    setLoadingTasks(true);
    try {
      const tasksData = await getTasksByProject(projectId);
      setTasks(tasksData || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Milestone name is required";
    }

    if (!formData.Type) {
      newErrors.Type = "Milestone type is required";
    }

    if (!formData.Status) {
      newErrors.Status = "Status is required";
    }

    if (formData.percentage) {
      const percentageValue = parseFloat(formData.percentage);
      if (
        isNaN(percentageValue) ||
        percentageValue < 0 ||
        percentageValue > 100
      ) {
        newErrors.percentage = "Percentage must be between 0 and 100";
      }
    }

    // Validate hour field
    if (formData.hour) {
      const hourValue = parseFloat(formData.hour);
      if (isNaN(hourValue) || hourValue < 0) {
        newErrors.hour = "Hours must be a positive number";
      }
    }

    if (formData.plannedStartDate && formData.plannedEndDate) {
      const startDate = new Date(formData.plannedStartDate);
      const endDate = new Date(formData.plannedEndDate);
      if (startDate > endDate) {
        newErrors.plannedEndDate = "End date cannot be before start date";
      }
    }

    if (formData.actualStartDate && formData.actualEndDate) {
      const startDate = new Date(formData.actualStartDate);
      const endDate = new Date(formData.actualEndDate);
      if (startDate > endDate) {
        newErrors.actualEndDate = "End date cannot be before start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        Type: formData.Type,
        percentage: formData.percentage
          ? parseFloat(formData.percentage)
          : null,
        plannedStartDate: formData.plannedStartDate || null,
        plannedEndDate: formData.plannedEndDate || null,
        actualStartDate: formData.actualStartDate || null,
        actualEndDate: formData.actualEndDate || null,
        prozmaPrId: formData.prozmaPrId,
        Status: formData.Status,
        description: formData.description,
        orgId: formData.orgId,
        taskAssigned:
          formData.taskAssigned.length > 0 ? formData.taskAssigned : null,
        hour: formData.hour ? parseFloat(formData.hour) : null, // Add hour to submit data
      };

      if (milestone) {
        const updateData = {
          ...submitData,
          milestoneMavroId: milestone.milestoneMavroId,
          milestonesId: milestone.milestonesId,
        };
        await updateMilestone(updateData, userName);
        toast.success("Milestone updated successfully");
      } else {
        await createMilestone(submitData, userName);
        toast.success("Milestone created successfully");
      }

      onMilestoneSaved();
      onHide();
    } catch (error) {
      console.error("Error saving milestone:", error);
      toast.error(error.message || "Failed to save milestone");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleTaskCheckboxChange = (taskId) => {
    const newSelectedTaskIds = new Set(selectedTaskIds);
    if (newSelectedTaskIds.has(taskId)) {
      newSelectedTaskIds.delete(taskId);
    } else {
      newSelectedTaskIds.add(taskId);
    }
    setSelectedTaskIds(newSelectedTaskIds);
  };

  const handleApplyTasks = () => {
    const selectedTasksArray = Array.from(selectedTaskIds);
    handleChange("taskAssigned", selectedTasksArray);
    setShowTaskDropdown(false);
  };

  const getTaskNameById = (taskId) => {
    const task = tasks.find((t) => t.taskId === taskId);
    return task ? task.taskName : `Task ${taskId}`;
  };

  const removeSelectedTask = (taskId) => {
    const newSelectedTaskIds = new Set(selectedTaskIds);
    newSelectedTaskIds.delete(taskId);
    setSelectedTaskIds(newSelectedTaskIds);
    const selectedTasksArray = Array.from(newSelectedTaskIds);
    handleChange("taskAssigned", selectedTasksArray);
  };

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const getSelectedTypeDisplayName = () => {
    if (!formData.Type) return "";
    const typeGuidLower = formData.Type.toLowerCase();
    return typeNameMap[typeGuidLower] || "Unknown Type";
  };

  const getSelectedStatusDisplayName = () => {
    if (!formData.Status) return "";
    const statusGuidLower = formData.Status.toLowerCase();
    return statusNameMap[statusGuidLower] || "Unknown Status";
  };

  const getStatusBadgeClass = (statusName) => {
    const statusMap = {
      Completed: "mm-status-completed",
      "In Progress": "mm-status-progress",
      "On Hold": "mm-status-hold",
      Cancelled: "mm-status-cancelled",
      Delayed: "mm-status-delayed",
      "Not Started": "mm-status-notstarted",
    };
    return statusMap[statusName] || "mm-status-default";
  };

  const filteredTasks = tasks.filter(
    (task) =>
      task.taskName?.toLowerCase().includes(searchTaskTerm.toLowerCase()) ||
      task.taskId?.toLowerCase().includes(searchTaskTerm.toLowerCase()),
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="mm-modal">
      <Modal.Header closeButton>
        <div className="mm-modal-header-content">
          <div className="mm-modal-icon">
            <FaFlag />
          </div>
          <div>
            <Modal.Title className="mm-modal-title">
              {milestone ? "Edit Milestone" : "Create New Milestone"}
            </Modal.Title>
            <p className="mm-modal-subtitle">
              {milestone
                ? "Update milestone details and track progress"
                : "Define a new milestone to track project progress"}
            </p>
          </div>
        </div>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="mm-modal-body">
          {errors.general && <Alert variant="danger">{errors.general}</Alert>}

          {/* Milestone Name & Type Row */}
          <Row className="mm-form-row">
            <Col md={7}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaFlag className="mm-label-icon" />
                  Milestone Name <span className="mm-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className="mm-form-control"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  isInvalid={!!errors.name}
                  placeholder="Enter milestone name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaProjectDiagram className="mm-label-icon" />
                  Type <span className="mm-required">*</span>
                </Form.Label>
                <Form.Select
                  className="mm-form-control"
                  value={formData.Type}
                  onChange={(e) => handleChange("Type", e.target.value)}
                  isInvalid={!!errors.Type}
                  disabled={loadingMetaData}
                >
                  <option value="">
                    {loadingMetaData ? "Loading types..." : "Select Type"}
                  </option>
                  {milestoneTypes.map((type) => (
                    <option
                      key={type.milestoneType_GUID}
                      value={type.milestoneType_GUID}
                    >
                      {type.milestoneType_Name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.Type}
                </Form.Control.Feedback>
                {formData.Type && !loadingMetaData && (
                  <Form.Text className="mm-form-text">
                    Selected: {getSelectedTypeDisplayName()}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Status & Percentage Row */}
          <Row className="mm-form-row">
            <Col md={5}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaCheck className="mm-label-icon" />
                  Status <span className="mm-required">*</span>
                </Form.Label>
                <Form.Select
                  className="mm-form-control"
                  value={formData.Status}
                  onChange={(e) => handleChange("Status", e.target.value)}
                  isInvalid={!!errors.Status}
                  disabled={loadingMetaData}
                >
                  <option value="">
                    {loadingMetaData ? "Loading statuses..." : "Select Status"}
                  </option>
                  {milestoneStatuses.map((status) => (
                    <option key={status.statusGuid} value={status.statusGuid}>
                      {status.statusName}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.Status}
                </Form.Control.Feedback>
                {formData.Status && !loadingMetaData && (
                  <span
                    className={`mm-status-preview ${getStatusBadgeClass(getSelectedStatusDisplayName())}`}
                  >
                    {getSelectedStatusDisplayName()}
                  </span>
                )}
              </Form.Group>
            </Col>

            <Col md={7}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaPercentage className="mm-label-icon" />
                  Completion Percentage
                </Form.Label>
                <div className="mm-percentage-wrapper">
                  <Form.Control
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    className="mm-form-control mm-percentage-input"
                    value={formData.percentage}
                    onChange={(e) => handleChange("percentage", e.target.value)}
                    placeholder="0-100"
                    isInvalid={!!errors.percentage}
                  />
                  <span className="mm-percentage-symbol">%</span>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.percentage}
                </Form.Control.Feedback>
                <Form.Text className="mm-form-text">
                  Set milestone completion percentage
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* HOUR FIELD - Add this new row */}
          <Row className="mm-form-row">
            <Col md={12}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaClock className="mm-label-icon" />
                  Estimated Hours
                </Form.Label>
                <div className="mm-hour-wrapper">
                  <Form.Control
                    type="number"
                    step="0.5"
                    min="0"
                    className="mm-form-control mm-hour-input"
                    value={formData.hour}
                    onChange={(e) => handleChange("hour", e.target.value)}
                    placeholder="Enter estimated hours"
                    isInvalid={!!errors.hour}
                  />
                  <span className="mm-hour-symbol">hours</span>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.hour}
                </Form.Control.Feedback>
                <Form.Text className="mm-form-text">
                  Estimated hours required for this milestone
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Assigned Tasks Section */}
          <Form.Group className="mm-form-group mm-tasks-group">
            <Form.Label className="mm-form-label">
              <FaTasks className="mm-label-icon" />
              Assigned Tasks
            </Form.Label>

            {/* Selected Tasks Display */}
            <div className="mm-selected-tasks">
              {formData.taskAssigned.length > 0 ? (
                <div className="mm-tasks-badges">
                  {formData.taskAssigned.map((taskId) => (
                    <Badge key={taskId} className="mm-task-badge">
                      <span className="mm-task-badge-text">
                        {getTaskNameById(taskId)}
                      </span>
                      <button
                        type="button"
                        className="mm-task-badge-remove"
                        onClick={() => removeSelectedTask(taskId)}
                      >
                        <FaTimes />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="mm-no-tasks">
                  <FaTasks className="mm-no-tasks-icon" />
                  <span>No tasks selected for this milestone</span>
                </div>
              )}
            </div>

            {/* Task Selection Dropdown */}
            <div className="mm-task-selector">
              <Button
                type="button"
                variant="outline-primary"
                onClick={() => setShowTaskDropdown(!showTaskDropdown)}
                disabled={loadingTasks || tasks.length === 0}
                className="mm-select-tasks-btn"
              >
                {loadingTasks ? (
                  <>Loading tasks...</>
                ) : (
                  <>
                    <FaTasks className="me-2" />
                    {showTaskDropdown ? "Hide Tasks" : "Select Tasks"}
                    <span className="mm-selected-count">
                      ({selectedTaskIds.size} selected)
                    </span>
                  </>
                )}
              </Button>

              {showTaskDropdown && (
                <div className="mm-task-dropdown">
                  <div className="mm-task-dropdown-header">
                    <Form.Control
                      type="text"
                      placeholder="Search tasks by name or ID..."
                      value={searchTaskTerm}
                      onChange={(e) => setSearchTaskTerm(e.target.value)}
                      className="mm-task-search"
                    />
                    <div className="mm-task-dropdown-actions">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          setSelectedTaskIds(new Set(formData.taskAssigned))
                        }
                      >
                        Reset
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        onClick={handleApplyTasks}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                  <div className="mm-task-list">
                    {filteredTasks.length > 0 ? (
                      filteredTasks.map((task) => (
                        <div key={task.taskId} className="mm-task-item">
                          <Form.Check
                            type="checkbox"
                            id={`task-${task.taskId}`}
                            label={
                              <div className="mm-task-item-content">
                                <span className="mm-task-item-name">
                                  {task.taskName}
                                </span>
                                <div className="mm-task-item-meta">
                                  <span className="mm-task-item-id">
                                    {task.taskId}
                                  </span>
                                  {task.taskStatus && (
                                    <span className="mm-task-item-status">
                                      {task.taskStatus}
                                    </span>
                                  )}
                                  {task.taskPriority && (
                                    <span
                                      className={`mm-task-item-priority mm-priority-${task.taskPriority?.toLowerCase()}`}
                                    >
                                      {task.taskPriority}
                                    </span>
                                  )}
                                </div>
                              </div>
                            }
                            checked={selectedTaskIds.has(task.taskId)}
                            onChange={() =>
                              handleTaskCheckboxChange(task.taskId)
                            }
                          />
                        </div>
                      ))
                    ) : (
                      <div className="mm-task-empty">
                        No tasks found matching "{searchTaskTerm}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <Form.Text className="mm-form-text">
              Select tasks that belong to this milestone
            </Form.Text>
          </Form.Group>

          {/* Dates Section */}
          <div className="mm-dates-section">
            <div className="mm-dates-header">
              <FaCalendarAlt className="mm-dates-icon" />
              <span>Date Planning</span>
            </div>
            <Row>
              <Col md={6}>
                <Form.Group className="mm-form-group">
                  <Form.Label className="mm-form-label">
                    Planned Start Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className="mm-form-control"
                    value={formData.plannedStartDate}
                    onChange={(e) =>
                      handleChange("plannedStartDate", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mm-form-group">
                  <Form.Label className="mm-form-label">
                    Planned End Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className="mm-form-control"
                    value={formData.plannedEndDate}
                    onChange={(e) =>
                      handleChange("plannedEndDate", e.target.value)
                    }
                    min={formData.plannedStartDate}
                    isInvalid={!!errors.plannedEndDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.plannedEndDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group className="mm-form-group">
                  <Form.Label className="mm-form-label">
                    Actual Start Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className="mm-form-control"
                    value={formData.actualStartDate}
                    onChange={(e) =>
                      handleChange("actualStartDate", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mm-form-group">
                  <Form.Label className="mm-form-label">
                    Actual End Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className="mm-form-control"
                    value={formData.actualEndDate}
                    onChange={(e) =>
                      handleChange("actualEndDate", e.target.value)
                    }
                    min={formData.actualStartDate}
                    isInvalid={!!errors.actualEndDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.actualEndDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Description */}
          <Form.Group className="mm-form-group">
            <Form.Label className="mm-form-label">
              <FaInfoCircle className="mm-label-icon" />
              Description
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="mm-form-control mm-textarea"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter milestone description..."
            />
          </Form.Group>

          {/* Organization & Project IDs */}
          <Row>
            <Col md={6}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaBuilding className="mm-label-icon" />
                  Organization ID
                </Form.Label>
                <Form.Control
                  type="text"
                  className="mm-form-control mm-readonly"
                  value={formData.orgId}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mm-form-group">
                <Form.Label className="mm-form-label">
                  <FaProjectDiagram className="mm-label-icon" />
                  Project ID
                </Form.Label>
                <Form.Control
                  type="text"
                  className="mm-form-control mm-readonly"
                  value={formData.prozmaPrId}
                  readOnly
                  disabled={!!projectId}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="mm-modal-footer">
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={loading}
            className="mm-btn mm-btn-secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className="mm-btn mm-btn-primary"
          >
            {loading ? (
              <>
                <span className="mm-spinner"></span>
                Saving...
              </>
            ) : (
              <>
                {milestone ? (
                  <>
                    <FaCheck className="me-2" />
                    Update Milestone
                  </>
                ) : (
                  <>
                    <FaFlag className="me-2" />
                    Create Milestone
                  </>
                )}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MilestoneModal;

// import { useState, useEffect } from "react";
// import { Modal, Button, Form, Alert, Row, Col, Badge } from "react-bootstrap";
// import { toast } from "react-toastify";
// import {
//   createMilestone,
//   updateMilestone,
//   getMilestoneTypes,
//   getMilestoneStatuses,
//   getMilestoneTypesByOrg,
//   getMilestoneStatusesByOrg,
// } from "../Api/MilestoneMaster";
// import { getTasksByProject } from "../Api/TaskMaster";
// import {
//   FaFlag,
//   FaTasks,
//   FaCalendarAlt,
//   FaPercentage,
//   FaInfoCircle,
//   FaProjectDiagram,
//   FaBuilding,
//   FaUser,
//   FaTimes,
//   FaCheck,
//   FaArrowRight,
// } from "react-icons/fa";
// import "./MilestoneModal.css";

// const MilestoneModal = ({
//   show,
//   onHide,
//   milestone,
//   projectId,
//   onMilestoneSaved,
// }) => {
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;
//   const orgId = userDetails?.organisationId;
//   const userName = userDetails?.username || "System";

//   const [formData, setFormData] = useState({
//     name: "",
//     Type: "",
//     percentage: "",
//     plannedStartDate: "",
//     plannedEndDate: "",
//     actualStartDate: "",
//     actualEndDate: "",
//     prozmaPrId: projectId || "",
//     Status: "",
//     description: "",
//     orgId: String(orgId) || "",
//     taskAssigned: [],
//     hour: "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [tasks, setTasks] = useState([]);
//   const [loadingTasks, setLoadingTasks] = useState(false);
//   const [showTaskDropdown, setShowTaskDropdown] = useState(false);
//   const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
//   const [searchTaskTerm, setSearchTaskTerm] = useState("");

//   const [milestoneTypes, setMilestoneTypes] = useState([]);
//   const [milestoneStatuses, setMilestoneStatuses] = useState([]);
//   const [loadingMetaData, setLoadingMetaData] = useState(false);

//   const [typeNameMap, setTypeNameMap] = useState({});
//   const [statusNameMap, setStatusNameMap] = useState({});

//   useEffect(() => {
//     if (show && orgId) {
//       fetchMilestoneTypes();
//       fetchMilestoneStatuses();
//     }
//   }, [show, orgId]);

//   const fetchMilestoneTypes = async () => {
//     if (!orgId) {
//       console.warn("No orgId available to fetch milestone types");
//       return;
//     }

//     try {
//       setLoadingMetaData(true);
//       const types = await getMilestoneTypesByOrg(orgId);
//       const typesArray = Array.isArray(types) ? types : types?.data || [];
//       setMilestoneTypes(typesArray);

//       const typeMap = {};
//       typesArray.forEach((type) => {
//         const guid = type.milestoneType_GUID || type.guid;
//         const name = type.milestoneType_Name || type.name;
//         if (guid && name) {
//           typeMap[guid.toLowerCase()] = name;
//         }
//       });
//       setTypeNameMap(typeMap);
//     } catch (error) {
//       console.error("Error fetching milestone types:", error);
//       toast.error("Failed to load milestone types");
//     } finally {
//       setLoadingMetaData(false);
//     }
//   };

//   // const fetchMilestoneTypes = async () => {
//   //   try {
//   //     setLoadingMetaData(true);
//   //     const types = await getMilestoneTypes();
//   //     const typesArray = Array.isArray(types) ? types : types?.data || [];
//   //     setMilestoneTypes(typesArray);

//   //     const typeMap = {};
//   //     typesArray.forEach((type) => {
//   //       if (type.milestoneType_GUID && type.milestoneType_Name) {
//   //         typeMap[type.milestoneType_GUID.toLowerCase()] = type.milestoneType_Name;
//   //       }
//   //     });
//   //     setTypeNameMap(typeMap);
//   //   } catch (error) {
//   //     console.error("Error fetching milestone types:", error);
//   //     toast.error("Failed to load milestone types");
//   //   } finally {
//   //     setLoadingMetaData(false);
//   //   }
//   // };

//   // const fetchMilestoneStatuses = async () => {
//   //   try {
//   //     setLoadingMetaData(true);
//   //     const statuses = await getMilestoneStatuses();
//   //     const statusesArray = Array.isArray(statuses)
//   //       ? statuses
//   //       : statuses?.data || [];
//   //     setMilestoneStatuses(statusesArray);

//   //     const statusMap = {};
//   //     statusesArray.forEach((status) => {
//   //       if (status.statusGuid && status.statusName) {
//   //         statusMap[status.statusGuid.toLowerCase()] = status.statusName;
//   //       }
//   //     });
//   //     setStatusNameMap(statusMap);
//   //   } catch (error) {
//   //     console.error("Error fetching milestone statuses:", error);
//   //     toast.error("Failed to load milestone statuses");
//   //   } finally {
//   //     setLoadingMetaData(false);
//   //   }
//   // };

//   // Update the fetchMilestoneStatuses function
//   const fetchMilestoneStatuses = async () => {
//     if (!orgId) {
//       console.warn("No orgId available to fetch milestone statuses");
//       return;
//     }

//     try {
//       setLoadingMetaData(true);
//       const statuses = await getMilestoneStatusesByOrg(orgId);
//       const statusesArray = Array.isArray(statuses)
//         ? statuses
//         : statuses?.data || [];
//       setMilestoneStatuses(statusesArray);

//       const statusMap = {};
//       statusesArray.forEach((status) => {
//         const guid = status.statusGuid || status.guid;
//         const name = status.statusName || status.name;
//         if (guid && name) {
//           statusMap[guid.toLowerCase()] = name;
//         }
//       });
//       setStatusNameMap(statusMap);
//     } catch (error) {
//       console.error("Error fetching milestone statuses:", error);
//       toast.error("Failed to load milestone statuses");
//     } finally {
//       setLoadingMetaData(false);
//     }
//   };

//   useEffect(() => {
//     if (show && projectId) {
//       fetchTasks();
//     }
//   }, [show, projectId]);

//   // In the useEffect that populates formData when editing a milestone
//   useEffect(() => {
//     if (milestone) {
//       let taskAssignedArray = [];

//       if (
//         milestone.taskAssignedList &&
//         Array.isArray(milestone.taskAssignedList)
//       ) {
//         taskAssignedArray = milestone.taskAssignedList;
//       } else if (
//         milestone.taskAssigned &&
//         Array.isArray(milestone.taskAssigned)
//       ) {
//         taskAssignedArray = milestone.taskAssigned;
//       } else if (
//         milestone.taskAssigned &&
//         typeof milestone.taskAssigned === "string"
//       ) {
//         taskAssignedArray = milestone.taskAssigned
//           .split(",")
//           .map((t) => t.trim())
//           .filter((t) => t);
//       }

//       let typeGuid = "";
//       if (milestone.Type) {
//         typeGuid = milestone.Type;
//       } else if (milestone.type) {
//         typeGuid = milestone.type;
//       } else if (milestone.milestoneType_GUID) {
//         typeGuid = milestone.milestoneType_GUID;
//       }

//       let statusGuid = "";
//       if (milestone.Status) {
//         statusGuid = milestone.Status;
//       } else if (milestone.status) {
//         statusGuid = milestone.status;
//       } else if (milestone.statusGuid) {
//         statusGuid = milestone.statusGuid;
//       }

//       // FIX: Extract date string directly without converting through Date object
//       const formatDateFromApi = (dateValue) => {
//         if (!dateValue) return "";

//         // If it's already in YYYY-MM-DD format, return as is
//         if (
//           typeof dateValue === "string" &&
//           /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
//         ) {
//           return dateValue;
//         }

//         // If it's a date string with time (YYYY-MM-DDTHH:MM:SS), extract just the date part
//         if (typeof dateValue === "string" && dateValue.includes("T")) {
//           return dateValue.split("T")[0];
//         }

//         // If it's a Date object or needs conversion, handle carefully
//         try {
//           // Create date in UTC to avoid timezone shift
//           const date = new Date(dateValue);
//           if (isNaN(date.getTime())) return "";
//           // Format as YYYY-MM-DD in UTC
//           return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
//         } catch {
//           return "";
//         }
//       };

//       setFormData({
//         name: milestone.name || "",
//         Type: typeGuid,
//         percentage: milestone.percentage || "",
//         plannedStartDate: formatDateFromApi(milestone.plannedStartDate),
//         plannedEndDate: formatDateFromApi(milestone.plannedEndDate),
//         actualStartDate: formatDateFromApi(milestone.actualStartDate),
//         actualEndDate: formatDateFromApi(milestone.actualEndDate),
//         prozmaPrId: milestone.prozmaPrId || projectId || "",
//         Status: statusGuid,
//         description: milestone.description || "",
//         orgId: milestone.orgId || String(orgId) || "",
//         taskAssigned: taskAssignedArray,
//         hour:
//           milestone.hour !== undefined && milestone.hour !== null
//             ? milestone.hour
//             : "", // Add this line
//       });

//       setSelectedTaskIds(new Set(taskAssignedArray));
//     } else {
//       // Reset form for new milestone
//       setFormData({
//         name: "",
//         Type: "",
//         percentage: "",
//         plannedStartDate: "",
//         plannedEndDate: "",
//         actualStartDate: "",
//         actualEndDate: "",
//         prozmaPrId: projectId || "",
//         Status: "",
//         description: "",
//         orgId: String(orgId) || "",
//         taskAssigned: [],
//         hour: "", // Add this line
//       });
//       setSelectedTaskIds(new Set());
//     }
//     setErrors({});
//     setSearchTaskTerm("");
//   }, [milestone, show, projectId, orgId]);

//   const fetchTasks = async () => {
//     if (!projectId) return;

//     setLoadingTasks(true);
//     try {
//       const tasksData = await getTasksByProject(projectId);
//       setTasks(tasksData || []);
//     } catch (error) {
//       console.error("Error fetching tasks:", error);
//       toast.error("Failed to load tasks");
//       setTasks([]);
//     } finally {
//       setLoadingTasks(false);
//     }
//   };

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.name.trim()) {
//       newErrors.name = "Milestone name is required";
//     }

//     if (!formData.Type) {
//       newErrors.Type = "Milestone type is required";
//     }

//     if (!formData.Status) {
//       newErrors.Status = "Status is required";
//     }

//     if (formData.percentage) {
//       const percentageValue = parseFloat(formData.percentage);
//       if (
//         isNaN(percentageValue) ||
//         percentageValue < 0 ||
//         percentageValue > 100
//       ) {
//         newErrors.percentage = "Percentage must be between 0 and 100";
//       }
//     }

//     if (formData.plannedStartDate && formData.plannedEndDate) {
//       const startDate = new Date(formData.plannedStartDate);
//       const endDate = new Date(formData.plannedEndDate);
//       if (startDate > endDate) {
//         newErrors.plannedEndDate = "End date cannot be before start date";
//       }
//     }

//     if (formData.actualStartDate && formData.actualEndDate) {
//       const startDate = new Date(formData.actualStartDate);
//       const endDate = new Date(formData.actualEndDate);
//       if (startDate > endDate) {
//         newErrors.actualEndDate = "End date cannot be before start date";
//       }
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setLoading(true);
//     try {
//       const submitData = {
//         name: formData.name,
//         Type: formData.Type,
//         percentage: formData.percentage
//           ? parseFloat(formData.percentage)
//           : null,
//         plannedStartDate: formData.plannedStartDate || null,
//         plannedEndDate: formData.plannedEndDate || null,
//         actualStartDate: formData.actualStartDate || null,
//         actualEndDate: formData.actualEndDate || null,
//         prozmaPrId: formData.prozmaPrId,
//         Status: formData.Status,
//         description: formData.description,
//         orgId: formData.orgId,
//         taskAssigned:
//           formData.taskAssigned.length > 0 ? formData.taskAssigned : null,
//       };

//       if (milestone) {
//         const updateData = {
//           ...submitData,
//           milestoneMavroId: milestone.milestoneMavroId,
//           milestonesId: milestone.milestonesId,
//         };
//         await updateMilestone(updateData, userName);
//         toast.success("Milestone updated successfully");
//       } else {
//         await createMilestone(submitData, userName);
//         toast.success("Milestone created successfully");
//       }

//       onMilestoneSaved();
//       onHide();
//     } catch (error) {
//       console.error("Error saving milestone:", error);
//       toast.error(error.message || "Failed to save milestone");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChange = (field, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));

//     if (errors[field]) {
//       setErrors((prev) => ({
//         ...prev,
//         [field]: "",
//       }));
//     }
//   };

//   const handleTaskCheckboxChange = (taskId) => {
//     const newSelectedTaskIds = new Set(selectedTaskIds);
//     if (newSelectedTaskIds.has(taskId)) {
//       newSelectedTaskIds.delete(taskId);
//     } else {
//       newSelectedTaskIds.add(taskId);
//     }
//     setSelectedTaskIds(newSelectedTaskIds);
//   };

//   const handleApplyTasks = () => {
//     const selectedTasksArray = Array.from(selectedTaskIds);
//     handleChange("taskAssigned", selectedTasksArray);
//     setShowTaskDropdown(false);
//   };

//   const getTaskNameById = (taskId) => {
//     const task = tasks.find((t) => t.taskId === taskId);
//     return task ? task.taskName : `Task ${taskId}`;
//   };

//   const removeSelectedTask = (taskId) => {
//     const newSelectedTaskIds = new Set(selectedTaskIds);
//     newSelectedTaskIds.delete(taskId);
//     setSelectedTaskIds(newSelectedTaskIds);
//     const selectedTasksArray = Array.from(newSelectedTaskIds);
//     handleChange("taskAssigned", selectedTasksArray);
//   };

//   const getTodayDate = () => {
//     return new Date().toISOString().split("T")[0];
//   };

//   const getSelectedTypeDisplayName = () => {
//     if (!formData.Type) return "";
//     const typeGuidLower = formData.Type.toLowerCase();
//     return typeNameMap[typeGuidLower] || "Unknown Type";
//   };

//   const getSelectedStatusDisplayName = () => {
//     if (!formData.Status) return "";
//     const statusGuidLower = formData.Status.toLowerCase();
//     return statusNameMap[statusGuidLower] || "Unknown Status";
//   };

//   const getStatusBadgeClass = (statusName) => {
//     const statusMap = {
//       Completed: "mm-status-completed",
//       "In Progress": "mm-status-progress",
//       "On Hold": "mm-status-hold",
//       Cancelled: "mm-status-cancelled",
//       Delayed: "mm-status-delayed",
//       "Not Started": "mm-status-notstarted",
//     };
//     return statusMap[statusName] || "mm-status-default";
//   };

//   const filteredTasks = tasks.filter(
//     (task) =>
//       task.taskName?.toLowerCase().includes(searchTaskTerm.toLowerCase()) ||
//       task.taskId?.toLowerCase().includes(searchTaskTerm.toLowerCase()),
//   );

//   return (
//     <Modal show={show} onHide={onHide} size="lg" centered className="mm-modal">
//       <Modal.Header closeButton>
//         <div className="mm-modal-header-content">
//           <div className="mm-modal-icon">
//             <FaFlag />
//           </div>
//           <div>
//             <Modal.Title className="mm-modal-title">
//               {milestone ? "Edit Milestone" : "Create New Milestone"}
//             </Modal.Title>
//             <p className="mm-modal-subtitle">
//               {milestone
//                 ? "Update milestone details and track progress"
//                 : "Define a new milestone to track project progress"}
//             </p>
//           </div>
//         </div>
//       </Modal.Header>

//       <Form onSubmit={handleSubmit}>
//         <Modal.Body className="mm-modal-body">
//           {errors.general && <Alert variant="danger">{errors.general}</Alert>}

//           {/* Milestone Name & Type Row */}
//           <Row className="mm-form-row">
//             <Col md={7}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <FaFlag className="mm-label-icon" />
//                   Milestone Name <span className="mm-required">*</span>
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   className="mm-form-control"
//                   value={formData.name}
//                   onChange={(e) => handleChange("name", e.target.value)}
//                   isInvalid={!!errors.name}
//                   placeholder="Enter milestone name"
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.name}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>

//             <Col md={5}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <FaProjectDiagram className="mm-label-icon" />
//                   Type <span className="mm-required">*</span>
//                 </Form.Label>
//                 <Form.Select
//                   className="mm-form-control"
//                   value={formData.Type}
//                   onChange={(e) => handleChange("Type", e.target.value)}
//                   isInvalid={!!errors.Type}
//                   disabled={loadingMetaData}
//                 >
//                   <option value="">
//                     {loadingMetaData ? "Loading types..." : "Select Type"}
//                   </option>
//                   {milestoneTypes.map((type) => (
//                     <option
//                       key={type.milestoneType_GUID}
//                       value={type.milestoneType_GUID}
//                     >
//                       {type.milestoneType_Name}
//                     </option>
//                   ))}
//                 </Form.Select>
//                 <Form.Control.Feedback type="invalid">
//                   {errors.Type}
//                 </Form.Control.Feedback>
//                 {formData.Type && !loadingMetaData && (
//                   <Form.Text className="mm-form-text">
//                     Selected: {getSelectedTypeDisplayName()}
//                   </Form.Text>
//                 )}
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row className="mm-form-row">
//             <Col md={12}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <i className="fas fa-clock"></i>
//                   Estimated Hours
//                 </Form.Label>
//                 <div className="mm-hour-wrapper">
//                   <Form.Control
//                     type="number"
//                     step="0.5"
//                     min="0"
//                     className="mm-form-control mm-hour-input"
//                     value={formData.hour}
//                     onChange={(e) => handleChange("hour", e.target.value)}
//                     placeholder="Enter estimated hours"
//                   />
//                   <span className="mm-hour-symbol">hours</span>
//                 </div>
//                 <Form.Text className="mm-form-text">
//                   Estimated hours required for this milestone
//                 </Form.Text>
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Status & Percentage Row */}
//           <Row className="mm-form-row">
//             <Col md={5}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <FaCheck className="mm-label-icon" />
//                   Status <span className="mm-required">*</span>
//                 </Form.Label>
//                 <Form.Select
//                   className="mm-form-control"
//                   value={formData.Status}
//                   onChange={(e) => handleChange("Status", e.target.value)}
//                   isInvalid={!!errors.Status}
//                   disabled={loadingMetaData}
//                 >
//                   <option value="">
//                     {loadingMetaData ? "Loading statuses..." : "Select Status"}
//                   </option>
//                   {milestoneStatuses.map((status) => (
//                     <option key={status.statusGuid} value={status.statusGuid}>
//                       {status.statusName}
//                     </option>
//                   ))}
//                 </Form.Select>
//                 <Form.Control.Feedback type="invalid">
//                   {errors.Status}
//                 </Form.Control.Feedback>
//                 {formData.Status && !loadingMetaData && (
//                   <span
//                     className={`mm-status-preview ${getStatusBadgeClass(getSelectedStatusDisplayName())}`}
//                   >
//                     {getSelectedStatusDisplayName()}
//                   </span>
//                 )}
//               </Form.Group>
//             </Col>

//             <Col md={7}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <FaPercentage className="mm-label-icon" />
//                   Completion Percentage
//                 </Form.Label>
//                 <div className="mm-percentage-wrapper">
//                   <Form.Control
//                     type="number"
//                     step="1"
//                     min="0"
//                     max="100"
//                     className="mm-form-control mm-percentage-input"
//                     value={formData.percentage}
//                     onChange={(e) => handleChange("percentage", e.target.value)}
//                     placeholder="0-100"
//                     isInvalid={!!errors.percentage}
//                   />
//                   <span className="mm-percentage-symbol">%</span>
//                 </div>
//                 <Form.Control.Feedback type="invalid">
//                   {errors.percentage}
//                 </Form.Control.Feedback>
//                 <Form.Text className="mm-form-text">
//                   Set milestone completion percentage
//                 </Form.Text>
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Assigned Tasks Section */}
//           <Form.Group className="mm-form-group mm-tasks-group">
//             <Form.Label className="mm-form-label">
//               <FaTasks className="mm-label-icon" />
//               Assigned Tasks
//             </Form.Label>

//             {/* Selected Tasks Display */}
//             <div className="mm-selected-tasks">
//               {formData.taskAssigned.length > 0 ? (
//                 <div className="mm-tasks-badges">
//                   {formData.taskAssigned.map((taskId) => (
//                     <Badge key={taskId} className="mm-task-badge">
//                       <span className="mm-task-badge-text">
//                         {getTaskNameById(taskId)}
//                       </span>
//                       <button
//                         type="button"
//                         className="mm-task-badge-remove"
//                         onClick={() => removeSelectedTask(taskId)}
//                       >
//                         <FaTimes />
//                       </button>
//                     </Badge>
//                   ))}
//                 </div>
//               ) : (
//                 <div className="mm-no-tasks">
//                   <FaTasks className="mm-no-tasks-icon" />
//                   <span>No tasks selected for this milestone</span>
//                 </div>
//               )}
//             </div>

//             {/* Task Selection Dropdown */}
//             <div className="mm-task-selector">
//               <Button
//                 type="button"
//                 variant="outline-primary"
//                 onClick={() => setShowTaskDropdown(!showTaskDropdown)}
//                 disabled={loadingTasks || tasks.length === 0}
//                 className="mm-select-tasks-btn"
//               >
//                 {loadingTasks ? (
//                   <>Loading tasks...</>
//                 ) : (
//                   <>
//                     <FaTasks className="me-2" />
//                     {showTaskDropdown ? "Hide Tasks" : "Select Tasks"}
//                     <span className="mm-selected-count">
//                       ({selectedTaskIds.size} selected)
//                     </span>
//                   </>
//                 )}
//               </Button>

//               {showTaskDropdown && (
//                 <div className="mm-task-dropdown">
//                   <div className="mm-task-dropdown-header">
//                     <Form.Control
//                       type="text"
//                       placeholder="Search tasks by name or ID..."
//                       value={searchTaskTerm}
//                       onChange={(e) => setSearchTaskTerm(e.target.value)}
//                       className="mm-task-search"
//                     />
//                     <div className="mm-task-dropdown-actions">
//                       <Button
//                         type="button"
//                         size="sm"
//                         variant="secondary"
//                         onClick={() =>
//                           setSelectedTaskIds(new Set(formData.taskAssigned))
//                         }
//                       >
//                         Reset
//                       </Button>
//                       <Button
//                         type="button"
//                         size="sm"
//                         variant="primary"
//                         onClick={handleApplyTasks}
//                       >
//                         Apply
//                       </Button>
//                     </div>
//                   </div>
//                   <div className="mm-task-list">
//                     {filteredTasks.length > 0 ? (
//                       filteredTasks.map((task) => (
//                         <div key={task.taskId} className="mm-task-item">
//                           <Form.Check
//                             type="checkbox"
//                             id={`task-${task.taskId}`}
//                             label={
//                               <div className="mm-task-item-content">
//                                 <span className="mm-task-item-name">
//                                   {task.taskName}
//                                 </span>
//                                 <div className="mm-task-item-meta">
//                                   <span className="mm-task-item-id">
//                                     {task.taskId}
//                                   </span>
//                                   {task.taskStatus && (
//                                     <span className="mm-task-item-status">
//                                       {task.taskStatus}
//                                     </span>
//                                   )}
//                                   {task.taskPriority && (
//                                     <span
//                                       className={`mm-task-item-priority mm-priority-${task.taskPriority?.toLowerCase()}`}
//                                     >
//                                       {task.taskPriority}
//                                     </span>
//                                   )}
//                                 </div>
//                               </div>
//                             }
//                             checked={selectedTaskIds.has(task.taskId)}
//                             onChange={() =>
//                               handleTaskCheckboxChange(task.taskId)
//                             }
//                           />
//                         </div>
//                       ))
//                     ) : (
//                       <div className="mm-task-empty">
//                         No tasks found matching "{searchTaskTerm}"
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               )}
//             </div>
//             <Form.Text className="mm-form-text">
//               Select tasks that belong to this milestone
//             </Form.Text>
//           </Form.Group>

//           {/* Dates Section */}
//           <div className="mm-dates-section">
//             <div className="mm-dates-header">
//               <FaCalendarAlt className="mm-dates-icon" />
//               <span>Date Planning</span>
//             </div>
//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mm-form-group">
//                   <Form.Label className="mm-form-label">
//                     Planned Start Date
//                   </Form.Label>
//                   <Form.Control
//                     type="date"
//                     className="mm-form-control"
//                     value={formData.plannedStartDate}
//                     onChange={(e) =>
//                       handleChange("plannedStartDate", e.target.value)
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={6}>
//                 <Form.Group className="mm-form-group">
//                   <Form.Label className="mm-form-label">
//                     Planned End Date
//                   </Form.Label>
//                   <Form.Control
//                     type="date"
//                     className="mm-form-control"
//                     value={formData.plannedEndDate}
//                     onChange={(e) =>
//                       handleChange("plannedEndDate", e.target.value)
//                     }
//                     min={formData.plannedStartDate}
//                     isInvalid={!!errors.plannedEndDate}
//                   />
//                   <Form.Control.Feedback type="invalid">
//                     {errors.plannedEndDate}
//                   </Form.Control.Feedback>
//                 </Form.Group>
//               </Col>
//             </Row>
//             <Row className="mt-2">
//               <Col md={6}>
//                 <Form.Group className="mm-form-group">
//                   <Form.Label className="mm-form-label">
//                     Actual Start Date
//                   </Form.Label>
//                   <Form.Control
//                     type="date"
//                     className="mm-form-control"
//                     value={formData.actualStartDate}
//                     onChange={(e) =>
//                       handleChange("actualStartDate", e.target.value)
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={6}>
//                 <Form.Group className="mm-form-group">
//                   <Form.Label className="mm-form-label">
//                     Actual End Date
//                   </Form.Label>
//                   <Form.Control
//                     type="date"
//                     className="mm-form-control"
//                     value={formData.actualEndDate}
//                     onChange={(e) =>
//                       handleChange("actualEndDate", e.target.value)
//                     }
//                     min={formData.actualStartDate}
//                     isInvalid={!!errors.actualEndDate}
//                   />
//                   <Form.Control.Feedback type="invalid">
//                     {errors.actualEndDate}
//                   </Form.Control.Feedback>
//                 </Form.Group>
//               </Col>
//             </Row>
//           </div>

//           {/* Description */}
//           <Form.Group className="mm-form-group">
//             <Form.Label className="mm-form-label">
//               <FaInfoCircle className="mm-label-icon" />
//               Description
//             </Form.Label>
//             <Form.Control
//               as="textarea"
//               rows={3}
//               className="mm-form-control mm-textarea"
//               value={formData.description}
//               onChange={(e) => handleChange("description", e.target.value)}
//               placeholder="Enter milestone description..."
//             />
//           </Form.Group>

//           {/* Organization & Project IDs */}
//           <Row>
//             <Col md={6}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <FaBuilding className="mm-label-icon" />
//                   Organization ID
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   className="mm-form-control mm-readonly"
//                   value={formData.orgId}
//                   readOnly
//                   disabled
//                 />
//               </Form.Group>
//             </Col>
//             <Col md={6}>
//               <Form.Group className="mm-form-group">
//                 <Form.Label className="mm-form-label">
//                   <FaProjectDiagram className="mm-label-icon" />
//                   Project ID
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   className="mm-form-control mm-readonly"
//                   value={formData.prozmaPrId}
//                   readOnly
//                   disabled={!!projectId}
//                 />
//               </Form.Group>
//             </Col>
//           </Row>
//         </Modal.Body>

//         <Modal.Footer className="mm-modal-footer">
//           <Button
//             variant="secondary"
//             onClick={onHide}
//             disabled={loading}
//             className="mm-btn mm-btn-secondary"
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             type="submit"
//             disabled={loading}
//             className="mm-btn mm-btn-primary"
//           >
//             {loading ? (
//               <>
//                 <span className="mm-spinner"></span>
//                 Saving...
//               </>
//             ) : (
//               <>
//                 {milestone ? (
//                   <>
//                     <FaCheck className="me-2" />
//                     Update Milestone
//                   </>
//                 ) : (
//                   <>
//                     <FaFlag className="me-2" />
//                     Create Milestone
//                   </>
//                 )}
//               </>
//             )}
//           </Button>
//         </Modal.Footer>
//       </Form>
//     </Modal>
//   );
// };

// export default MilestoneModal;
